import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Variable, ChevronDown } from 'lucide-react';

// Available template variables
const LEAD_VARIABLES = [
  { value: '{FirstName}', label: 'Lead first name', key: 'first_name' },
  { value: '{LastName}', label: 'Lead last name', key: 'last_name' },
  { value: '{FullName}', label: 'Full name (First + Last)', key: 'full_name' },
  { value: '{Title}', label: 'Job title', key: 'title' },
  { value: '{Email}', label: 'Work email', key: 'work_email' },
  { value: '{Phone}', label: 'Phone number', key: 'mobile_phone' },
];

const COMPANY_VARIABLES = [
  { value: '{CompanyName}', label: 'Company name', key: 'company_name' },
  { value: '{Location}', label: 'Location name', key: 'location_name' },
  { value: '{City}', label: 'City', key: 'city' },
  { value: '{State}', label: 'State', key: 'state' },
];

const USER_VARIABLES = [
  { value: '{SenderName}', label: 'Your name', key: 'sender_name' },
  { value: '{SenderEmail}', label: 'Your email', key: 'sender_email' },
];

/**
 * Variables Helper Component
 * Dropdown to insert template variables into text fields
 * 
 * @param {Function} onInsert - Callback when a variable is selected
 * @param {boolean} compact - Use compact styling
 */
export default function VariablesHelper({ onInsert, compact = false }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (variable) => {
    if (onInsert) {
      onInsert(variable);
    }
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={compact ? 'sm' : 'default'}
          className={compact ? 'h-8 gap-1 text-xs' : 'gap-2'}
          data-testid="insert-variable-btn"
        >
          <Variable className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          Insert Variable
          <ChevronDown className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-slate-500 font-medium">
          Lead Fields
        </DropdownMenuLabel>
        {LEAD_VARIABLES.map((v) => (
          <DropdownMenuItem
            key={v.value}
            onClick={() => handleSelect(v.value)}
            className="flex justify-between items-center"
            data-testid={`var-${v.key}`}
          >
            <span className="font-mono text-indigo-600 text-sm">{v.value}</span>
            <span className="text-xs text-slate-500">{v.label}</span>
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuLabel className="text-xs text-slate-500 font-medium">
          Company Fields
        </DropdownMenuLabel>
        {COMPANY_VARIABLES.map((v) => (
          <DropdownMenuItem
            key={v.value}
            onClick={() => handleSelect(v.value)}
            className="flex justify-between items-center"
            data-testid={`var-${v.key}`}
          >
            <span className="font-mono text-indigo-600 text-sm">{v.value}</span>
            <span className="text-xs text-slate-500">{v.label}</span>
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuLabel className="text-xs text-slate-500 font-medium">
          Sender Fields
        </DropdownMenuLabel>
        {USER_VARIABLES.map((v) => (
          <DropdownMenuItem
            key={v.value}
            onClick={() => handleSelect(v.value)}
            className="flex justify-between items-center"
            data-testid={`var-${v.key}`}
          >
            <span className="font-mono text-indigo-600 text-sm">{v.value}</span>
            <span className="text-xs text-slate-500">{v.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Replace template variables with actual values
 * @param {string} text - Text containing variables
 * @param {Object} lead - Lead object
 * @param {Object} user - Current user object
 * @returns {string} - Text with variables replaced
 */
export function replaceVariables(text, lead, user = {}) {
  if (!text) return text;
  
  return text
    .replace(/\{FirstName\}/gi, lead?.first_name || '')
    .replace(/\{LastName\}/gi, lead?.last_name || '')
    .replace(/\{FullName\}/gi, `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim())
    .replace(/\{Title\}/gi, lead?.title || '')
    .replace(/\{Email\}/gi, lead?.work_email || lead?.email || lead?.personal_email || '')
    .replace(/\{Phone\}/gi, lead?.phone || lead?.mobile_phone || lead?.work_direct_phone || '')
    .replace(/\{CompanyName\}/gi, lead?.company_name || lead?.current_employer || '')
    .replace(/\{Location\}/gi, lead?.location_name || lead?.city || '')
    .replace(/\{City\}/gi, lead?.city || lead?.location_name || '')
    .replace(/\{State\}/gi, lead?.state || '')
    .replace(/\{SenderName\}/gi, user?.full_name || '')
    .replace(/\{SenderEmail\}/gi, user?.email || '');
}
